/** Current-situation helpers for Fast Report (Now vs Historical). */

export const EUROPE_REGION = 'Europe';
/** Display label for the Europe immediate-notification source. */
export const WAHIS_INFUR_LABEL = 'WAHIS-INFUR';
/** Neighbourhood RISP source (not WAHIS). */
export const RISP_SOURCE_LABEL = 'RISP (Risk Information Sharing Platform)';
/** Neighbourhood FAST / country-reported source (not WAHIS). */
export const COUNTRY_SHARED_SOURCE_LABEL = 'Data shared by the country';
export const EXCLUDED_DISEASES = new Set(['BEF']);

export type FastReportPeriod = { year: number; quarter: number };

/**
 * Format Source for neighbourhood (non-INFUR) rows.
 * RISP → full platform name; otherwise country-shared (WAHIS stripped — INFUR only).
 */
export function formatNeighbourhoodSource(
  raw: string | null | undefined
): string {
  const text = String(raw || '')
    .replace(/\s+/g, ' ')
    .replace(/:?\s*contentReference\[oaicite:\d+\]\{index=\d+\}/gi, '')
    .trim();

  if (/^RISP\b/i.test(text)) return RISP_SOURCE_LABEL;

  // Strip WAHIS/WOAH/ADNS — those stay on INFUR only
  const withoutWahis = text
    .replace(/\bOIE[-–—]?\s*WAHIS\b/gi, '')
    .replace(/\bWAHIS\b/gi, '')
    .replace(/\bWOAH\b/gi, '')
    .replace(/https?:\/\/wahis\.woah\.org\S*/gi, '')
    .replace(/\b(EU\s+)?ADNS\b/gi, '')
    .replace(/\bADIS\b/gi, '')
    .replace(/[;|,]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (
    !withoutWahis ||
    withoutWahis === '-' ||
    /data shared by( the)? country/i.test(text) ||
    /shared by the country/i.test(text)
  ) {
    return COUNTRY_SHARED_SOURCE_LABEL;
  }

  return withoutWahis;
}

/** Point outbreak from WAHIS INFUR (Europe Now map). */
export interface InfurOutbreakPoint {
  eventId?: number | null;
  reportId?: number | null;
  outbreakId?: number | null;
  outbreakReference?: string | null;
  nationalReference?: string | null;
  reportType?: string | null;
  reportStatus?: string | null;
  eventStatus?: string | null;
  country: string;
  disease: string;
  diseaseLabel?: string | null;
  reason?: string | null;
  adminDivision?: string | null;
  location?: string | null;
  locationApprox?: string | null;
  latitude: number;
  longitude: number;
  outbreakStartDate?: string | null;
  outbreakEndDate?: string | null;
  submissionDate?: string | null;
  eventStartDate?: string | null;
  epiUnitType?: string | null;
  speciesName?: string | null;
  isWild?: string | null;
  susceptible?: number | null;
  cases?: number | null;
  deaths?: number | null;
  killed?: number | null;
  slaughtered?: number | null;
  vaccinated?: number | null;
}

export function periodKey(year: number, quarter: number): string {
  return `${year}-Q${quarter}`;
}

export function formatPeriod(p: FastReportPeriod): string {
  return periodKey(p.year, p.quarter);
}

/** Distinct Year/Quarter pairs from data, newest first. */
export function listPublishedQuarters(
  rows: Array<{ Year?: number | null; Quarter?: number | null }>
): FastReportPeriod[] {
  const seen = new Set<string>();
  const out: FastReportPeriod[] = [];
  for (const row of rows) {
    const year = Number(row.Year);
    const quarter = Number(row.Quarter);
    if (!year || !quarter || quarter < 1 || quarter > 4) continue;
    const key = periodKey(year, quarter);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ year, quarter });
  }
  out.sort((a, b) => (a.year !== b.year ? b.year - a.year : b.quarter - a.quarter));
  return out;
}

/** Last N published FAST quarters (default 2) for the Now map. */
export function getLastPublishedQuarters(
  rows: Array<{ Year?: number | null; Quarter?: number | null }>,
  n = 2
): FastReportPeriod[] {
  return listPublishedQuarters(rows).slice(0, n);
}

export function isInPeriods(
  year: number | null | undefined,
  quarter: number | null | undefined,
  periods: FastReportPeriod[]
): boolean {
  const y = Number(year);
  const q = Number(quarter);
  return periods.some((p) => p.year === y && p.quarter === q);
}

/** Calendar quarter bounds (FAST uses Q1–Q4). */
export function quarterDateRange(
  year: number,
  quarter: number
): { start: string; end: string } {
  const ranges: Record<number, [string, string]> = {
    1: ['01-01', '03-31'],
    2: ['04-01', '06-30'],
    3: ['07-01', '09-30'],
    4: ['10-01', '12-31'],
  };
  const [start, end] = ranges[quarter] || ranges[1];
  return { start: `${year}-${start}`, end: `${year}-${end}` };
}

/** Whether an ISO date (YYYY-MM-DD) falls in a FAST year/quarter filter. */
export function infurDateMatchesPeriod(
  dateStr: string | null | undefined,
  yearFilter: string,
  quarterFilter: string
): boolean {
  if (!dateStr || dateStr.length < 10) return false;
  const prefix = dateStr.slice(0, 10);
  if (yearFilter !== 'all') {
    const year = Number(yearFilter);
    if (!prefix.startsWith(`${year}-`)) return false;
    if (quarterFilter !== 'all') {
      const quarter = Number(quarterFilter);
      const { start, end } = quarterDateRange(year, quarter);
      return prefix >= start && prefix <= end;
    }
  } else if (quarterFilter !== 'all') {
    const quarter = Number(quarterFilter);
    const month = Number(prefix.slice(5, 7));
    const q = month <= 3 ? 1 : month <= 6 ? 2 : month <= 9 ? 3 : 4;
    return q === quarter;
  }
  return true;
}

/** Current calendar semester label, e.g. 2026-H2. */
export function getCurrentSemesterLabel(now = new Date()): string {
  const half = now.getMonth() < 6 ? 'H1' : 'H2';
  return `${now.getFullYear()}-${half}`;
}

export function excludeBefDisease<T extends { Disease?: string | null }>(rows: T[]): T[] {
  return rows.filter((r) => r.Disease && !EXCLUDED_DISEASES.has(r.Disease));
}
